import { useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { format, parseISO } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "../../components/shared/controls/Modal";
import Select, { StylesConfig } from "react-select";
import { FormEvent, useEffect, useState } from "react";
import {
  getTenantVerification,
  submitTenantVerification,
  TenantVerificationPayload,
  verificationIsActive,
} from "../../services/tenantVerification";
import "./TenantVerification.css";

type VerificationOption = { value: string; label: string };
const optionLabel = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
const dropdownStyles: StylesConfig<VerificationOption, false> = {
  control: (base, state) =>
    Object.assign({}, base, {
      minHeight: 48,
      borderRadius: 12,
      backgroundColor: "transparent",
      borderColor: state.isFocused ? "#47bba9" : "#d0d5dd",
      boxShadow: state.isFocused ? "0 0 0 3px #47bba91f" : "none",
      cursor: "pointer",
      ":hover": { borderColor: "#47bba9" },
    }),
  valueContainer: (base) =>
    Object.assign({}, base, { padding: "8px 12px", fontSize: 14 }),
  singleValue: (base) => Object.assign({}, base, { color: "#344054" }),
  placeholder: (base) => Object.assign({}, base, { color: "#667085" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) =>
    Object.assign({}, base, {
      color: state.isFocused ? "#278577" : "#667085",
      paddingRight: 14,
    }),
  menu: (base) =>
    Object.assign({}, base, {
      borderRadius: 14,
      border: "1px solid #e4e7ec",
      boxShadow: "0 12px 32px #1018281a",
      overflow: "hidden",
      backgroundColor: "#fff",
    }),
  menuList: (base) => Object.assign({}, base, { padding: 6 }),
  option: (base, state) =>
    Object.assign({}, base, {
      borderRadius: 8,
      margin: "2px 0",
      padding: "11px 12px",
      fontSize: 14,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#def3ed"
        : state.isFocused
          ? "#f0faf7"
          : "#fff",
      color: state.isSelected ? "#237a6d" : "#344054",
      fontWeight: state.isSelected ? 600 : 400,
      ":active": { backgroundColor: "#def3ed" },
    }),
  menuPortal: (base) => Object.assign({}, base, { zIndex: 1100 }),
};

const emptyForm: TenantVerificationPayload = {
  dateOfBirth: "",
  employmentStatus: "",
  workAddress: "",
  incomeRange: "",
  guarantor: { name: "", relationship: "", phone: "", email: "", address: "" },
  officeGuarantor: { companyName: "", name: "", jobTitle: "", phone: "", email: "", address: "" },
};

export default function TenantVerification() {
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get("transaction") || undefined;
  const [rejected, setRejected] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [employmentProof, setEmploymentProof] = useState<File | null>(null);
  const [incomeProof, setIncomeProof] = useState<File | null>(null);
  const [hasVerification, setHasVerification] = useState(false);
  const [active, setActive] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedDocuments, setSavedDocuments] = useState({ employment: "", income: "" });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getTenantVerification(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setHasVerification(data != null && Object.keys(data).length > 0);
        const result = verificationIsActive(data);
        if (result === null)
          throw new Error(
            "We couldn't determine your verification status. Please try again.",
          );
        setActive(result);
        setRejected(data?.status === "rejected");
        setRejectionReason(data?.rejectionReason || "");
        setSubmitted(["pending", "pending_review", "under_review"].includes(data?.status));
        if (data) {
          setForm({
            dateOfBirth: data.dateOfBirth?.slice(0, 10) || "",
            employmentStatus: data.employmentStatus === "self_employed" ? "self-employed" : data.employmentStatus || "",
            workAddress: data.workAddress || "",
            incomeRange: data.incomeRange || "",
            guarantor: { ...emptyForm.guarantor, ...data.guarantor },
            officeGuarantor: { ...emptyForm.officeGuarantor, ...data.officeGuarantor },
          });
          setSavedDocuments({ employment: data.proofOfEmploymentUrl || "", income: data.proofOfIncomeUrl || "" });
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(
            err?.response?.data?.message ||
              err.message ||
              "Could not load verification status.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [retry]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || (hasVerification && !rejected) || submitted || active || loading) return;
    if (!form.employmentStatus || !form.incomeRange) {
      setError("Please select your employment status and income range.");
      return;
    }
    if (!employmentProof) {
      setError("Please select your proof of employment.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await submitTenantVerification({ ...form, transaction: transactionId }, {
        proofOfEmployment: employmentProof,
        proofOfIncome: incomeProof,
      });
      window.dispatchEvent(new Event("tenant-verification-updated"));
      setHasVerification(true);
      setRejected(false);
      setSubmitted(true);
      setShowSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.errors?.[0]?.message ||
          err?.response?.data?.message ||
          err.message ||
          "Could not submit verification. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const field = (
    key: keyof Omit<TenantVerificationPayload, "guarantor" | "officeGuarantor" | "transaction">,
    label: string,
    type = "text",
    options?: string[],
  ) => (
    <div className="tenant-verification__field" key={key}>
      <label htmlFor={`tenant-${key}`}>{label}*</label>
      {options ? (
        <Select<VerificationOption>
          inputId={`tenant-${key}`}
          name={key}
          aria-required="true"
          isSearchable={false}
          isDisabled={submitted || active}
          placeholder="Select"
          options={options.map((value) => ({
            value,
            label: optionLabel(value),
          }))}
          value={
            form[key]
              ? { value: form[key], label: optionLabel(form[key]) }
              : null
          }
          onChange={(option) =>
            setForm({ ...form, [key]: option?.value || "" })
          }
          styles={dropdownStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuPlacement="auto"
        />
      ) : type === "date" ? (
        <DatePicker
          id={`tenant-${key}`}
          disabled={submitted || active}
          selected={form[key] ? parseISO(form[key]) : null}
          onChange={(date: Date | null) => setForm({ ...form, [key]: date ? format(date, "yyyy-MM-dd") : "" })}
          dateFormat="dd/MM/yyyy"
          placeholderText="DD/MM/YYYY"
          maxDate={new Date()}
          showMonthDropdown
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={120}
          dropdownMode="scroll"
          calendarClassName="tenant-birth-calendar"
          wrapperClassName="tenant-birth-input"
          popperClassName="tenant-birth-popper"
          portalId="tenant-birth-calendar-portal"
          popperPlacement="bottom-start"
          showPopperArrow={false}
          required
          autoComplete="off"
          strictParsing
        />
      ) : (
        <input
          id={`tenant-${key}`}
          required
          type={type}
          value={form[key]}
          max={
            type === "date" ? new Date().toISOString().slice(0, 10) : undefined
          }
          placeholder={
            type === "url" ? "https://…" : `Enter ${label.toLowerCase()}`
          }
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        />
      )}
    </div>
  );

  const guarantorField = <G extends "guarantor" | "officeGuarantor">(
    group: G,
    key: keyof TenantVerificationPayload[G] & string,
    label: string,
    type = "text",
  ) => (
    <div
      className={`tenant-verification__field${key === "address" ? " tenant-verification__field--wide" : ""}`}
      key={`${group}-${key}`}
    >
      <label htmlFor={`${group}-${key}`}>{label}*</label>
      <input
        id={`${group}-${key}`}
        type={type}
        required
        value={(form[group] as Record<string, string>)[key]}
        placeholder={`Enter ${label.toLowerCase()}`}
        onChange={(e) =>
          setForm({
            ...form,
            [group]: { ...form[group], [key]: e.target.value },
          })
        }
      />
    </div>
  );

  const uploadField = (
    id: string,
    label: string,
    file: File | null,
    setFile: (file: File | null) => void,
  ) => (
    <div className="tenant-verification__field">
      <label htmlFor={`tenant-proof-${id}`}>{label}{id === "employment" ? "*" : " (optional)"}</label>
      {savedDocuments[id as keyof typeof savedDocuments] && <a href={savedDocuments[id as keyof typeof savedDocuments]} target="_blank" rel="noopener noreferrer">View submitted {label.toLowerCase()}</a>}
      {!submitted && !active && <div className="tenant-verification__upload">
        <span>{file?.name || "Upload document"}</span>
        <span aria-hidden="true">↥</span>
        <input
          id={`tenant-proof-${id}`}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          required={id === "employment"}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file && (!["image/jpeg", "image/png", "application/pdf"].includes(file.type) || file.size > 15 * 1024 * 1024)) {
              setError("Choose a PDF, JPG or PNG up to 15 MB.");
              event.target.value = ""; setFile(null); return;
            }
            setError(""); setFile(file || null);
          }}
        />
      </div>
      }
      {/* <small>Image or PDF</small> */}
    </div>
  );

  return (
    <section className="tenant-verification font-onest">
      <h4>Tenant Verification</h4>
      {loading && <p role="status">Checking your verification…</p>}
      {rejected && <div className="alert alert-warning" role="alert"><strong>Verification rejected</strong><p>{rejectionReason || "Please update your details and resubmit."}</p></div>}
      <p>Add a personal guarantor and an office guarantor. Both will be asked to sign an undertaking once your verification is approved, and only one of them needs to sign.</p>
      {active ? (
        <div className="alert alert-success" role="status">
          Verified ✓
        </div>
      ) : submitted ? (
        <div className="alert alert-info" role="status">
          Under review — your tenant verification has been submitted and is being reviewed.
        </div>
      ) : null}
        <>
          <form onSubmit={submit}>
            <fieldset disabled={submitted || active}>
              <div className="tenant-verification__grid">
                {field("dateOfBirth", "Date of Birth", "date")}
                {field("employmentStatus", "Employment Status", "text", [
                  "employed",
                  "self-employed",
                  "business-owner",
                  "unemployed",
                  "student",
                  "retired",
                ])}
                {field("workAddress", "Work Address")}
                {uploadField(
                  "employment",
                  "Proof of Employment",
                  employmentProof,
                  setEmploymentProof,
                )}
                {field("incomeRange", "Income Range", "text", [
                  "Below 1,000,000",
                  "1,000,000 - 5,000,000",
                  "5,000,000 - 10,000,000",
                  "Above 10,000,000",
                ])}
                {uploadField(
                  "income",
                  "Income Proof",
                  incomeProof,
                  setIncomeProof,
                )}
                <h5 className="tenant-verification__field--wide">Personal Guarantor</h5>
                {guarantorField("guarantor", "name", "Guarantor Name")}
                {guarantorField("guarantor", "relationship", "Relationship to Guarantor")}
                {guarantorField("guarantor", "phone", "Guarantor Contact Number", "tel")}
                {guarantorField("guarantor", "email", "Guarantor Email", "email")}
                {guarantorField("guarantor", "address", "Guarantor Address")}
                <h5 className="tenant-verification__field--wide">Office Guarantor</h5>
                {guarantorField("officeGuarantor", "companyName", "Company / Organisation")}
                {guarantorField("officeGuarantor", "name", "Guarantor Name")}
                {guarantorField("officeGuarantor", "jobTitle", "Job Title")}
                {guarantorField("officeGuarantor", "phone", "Guarantor Contact Number", "tel")}
                {guarantorField("officeGuarantor", "email", "Guarantor Email", "email")}
                {guarantorField("officeGuarantor", "address", "Office Address")}
              </div>
              {(!hasVerification || rejected) && !submitted && !active && <button
                className="btn tenant-verification__submit"
                type="submit"
                disabled={submitting || loading}
              >
                {submitting ? "Submitting…" : rejected ? "Resubmit Tenant Verification" : "Submit Tenant Verification"}
              </button>}
            </fieldset>
          </form>
        </>
      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}{" "}
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setRetry((value) => value + 1)}
          >
            Recheck status
          </button>
        </div>
      )}
      <Modal show={showSuccess} onClose={() => setShowSuccess(false)} size={680} className="tenant-verification-success">
        <div className="tenant-verification-success__body font-onest" role="status">
          <div className="tenant-verification-success__icon" aria-hidden="true">
            <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
              <path d="M7 41L16 17L31 32L7 41Z" fill="#145c41" />
              <g stroke="#145c41" strokeWidth="3" strokeLinecap="round">
                <path d="M23 24l7-7c3-3 5-4 8-2M21 16l3-4c2-3 0-5-2-7M29 23l5-2c3-1 5 1 6 3M18 12c3-3 1-5-1-7M32 10l3-4" />
              </g>
            </svg>
          </div>
          <h3>Tenant Verification submitted!</h3>
          <p>Your Tenant Verification submission has been successfully received and is currently undergoing review by one of our legal team members.</p>
        </div>
      </Modal>
    </section>
  );
}
